import { Button, Grid } from "@trussworks/react-uswds";
import { useSearchParams } from "react-router-dom";
import {
  SearchFilters,
  getFiltersWithOptionalCleared,
  getFiltersFromSearchParams,
  getAppliedOptionalFiltersCount,
} from "../../../../types/v1_schemas";
import { ReactComponent as Close } from "../../../../images/close.svg";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { logEvent, AnalyticsAction } from "../../../../utils/analytics";
import {
  //getDayOptions,
  getLookupOptions,
  optionWithId,
} from "../../../../utils/lookup";
import { toggleDayFilters, toggleItemInFilterList, toggleItemsInFilterList } from "../../../../utils/filters/shared";
import { QueryLookupTablesApi } from "../../../../utils/QueryHelper";
import i18n from "../../../../i18n";
import { AxiosResponse } from "axios";
import { CrisisContext } from "../../../../App";

type DesktopControlToggleProps = {
  name: string;
  onClick: () => void;
};

function DesktopControlToggle({ name, onClick }: DesktopControlToggleProps) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      onClick={onClick}
      unstyled
      className="margin-right-2 padding-y-05 text-no-underline text-dark-blue width-auto"
      aria-label={`${t("clear")} ${name}`}
    >
      <span className="usa-sr-only">{t("clear")} </span>
      {name}
      <Close
        height={10}
        className="margin-left-05"
        aria-label={`${t("clear")} ${name}`}
      />
    </Button>
  );
}

type ApiServiceProvided = {
  id: number;
  categoryId: number;
  plainLanguageEn: string;
  plainLanguageEs: string;
  textEn: string;
  textEs: string;
};

export type ResponseServiceProvided = {
  id: string;
  categoryId: number;
  plainLanguage: string;
  text: string;
};

type ControlTogglesProps = {
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
};

function ControlToggles({ setFilters }: ControlTogglesProps) {
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  let filters = getFiltersFromSearchParams(searchParams);

  const [accessibiltyOptions] = useState(() => getLookupOptions("accessibility"));
  const [ageOptions] = useState(() => getLookupOptions("age"));
  const [communityOptions] = useState(() => getLookupOptions("community"));
  const [languageOptions] = useState(() => getLookupOptions("languages"));
  const [paymentOptions] = useState(() => getLookupOptions("payment"));
  const [telehealthOptions] = useState(() => getLookupOptions("telehealth"));
  const [dayOptions] = useState(() => ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]);

  const [responseServices, setResponseServices] = useState(new Array<ResponseServiceProvided>());
  
  const { crisisSelectedValues, setCrisisSelectedValues } = useContext(CrisisContext);

  const responseFn = (response: AxiosResponse) => {
    setResponseServices(
      response.data?.servicesProvided.reduce((acc: ResponseServiceProvided[], service: ApiServiceProvided) => {
        let index = -1;
        if (!acc || acc.length > 0)
          index = acc.findIndex(s => s.text === (i18n.language === "es" ? service.textEs : service.textEn));
        if (index === -1) {
          acc.push({
            id: service.id.toString(),
            categoryId: service.categoryId,
            plainLanguage: i18n.language === "es" ? service.plainLanguageEs : service.plainLanguageEn,
            text: i18n.language === "es" ? service.textEs : service.textEn
          });
          return acc;
        } else {
          acc[index].id += `,${service.id.toString()}`;
          return acc;
        }
      }, []));
  }

  const errorFn = (error: any) => {
    console.error("Error fetching services provided:", error);
  }
  
  useEffect(() => {
    QueryLookupTablesApi(
      {
        IncludeServicesProvided: true,
        language: i18n.language
      },
      new AbortController().signal,
      responseFn, errorFn);
  }, [i18n.language]);

  // Check if any optional (i.e. besides distance & location) filters are applied
  const optionalFiltersApplied = !!getAppliedOptionalFiltersCount(filters);

  if (!optionalFiltersApplied) return <></>;

  return (
    <Grid
      row
      className="margin-bottom-2 flex-align-baseline"
      id="active-filters-section"
    >
      <span className="text-bold text-dark-blue margin-right-2">
        {t("filteredBy")}:
      </span>
      {(!!filters.accessibilitySupportIds &&
        filters.accessibilitySupportIds.length &&
        accessibiltyOptions &&
        accessibiltyOptions.length &&
        accessibiltyOptions
          .filter(
            (item) =>
              filters.accessibilitySupportIds &&
              filters.accessibilitySupportIds.length &&
              item.id !== undefined &&
              filters.accessibilitySupportIds.includes(item.id.toString())
          )
          .map((option: optionWithId) => (
            <DesktopControlToggle
              key={option.id}
              name={t(option.translate.replace("accessibilityValues", "accessibilityShortValues"))}
              onClick={() => {
                setFilters({
                  ...(filters as SearchFilters),
                  accessibilitySupportIds: toggleItemInFilterList(
                    filters.accessibilitySupportIds as any[],
                    option.id.toString()
                  ) as [],
                });
              }}
            />
          ))) ||
        ""}

      {(!!filters.patientAgeGroupIds &&
        filters.patientAgeGroupIds.length &&
        ageOptions &&
        ageOptions.length &&
        ageOptions
          .filter(
            (item) =>
              filters.patientAgeGroupIds &&
              filters.patientAgeGroupIds.length &&
              item.id !== undefined &&
              filters.patientAgeGroupIds.includes(item.id.toString())
          )
          .map((ageGroup: optionWithId) => (
            <DesktopControlToggle
              key={ageGroup.id}
              name={t(ageGroup.translate)}
              onClick={() => {
                setFilters({
                  ...(filters as SearchFilters),
                  patientAgeGroupIds: 
                  toggleItemInFilterList(
                        filters.patientAgeGroupIds as any[],
                        ageGroup.id.toString()
                      ) as []
                });
              }}
            />
          ))) ||
        ""}
     

      {(!!filters.paymentTypeIds &&
        filters.paymentTypeIds.length &&
        paymentOptions.length &&
        paymentOptions
          .filter(
            (item) =>
              filters.paymentTypeIds &&
              filters.paymentTypeIds.length &&
              item.id !== undefined &&
              filters.paymentTypeIds.includes(item.id.toString())
          )
          .map((fees: optionWithId) => (
            <DesktopControlToggle
              key={fees.id}
              name={t(fees.translate)}
              onClick={() =>
                setFilters({
                  ...(filters as SearchFilters),
                  paymentTypeIds: toggleItemInFilterList(
                    filters.paymentTypeIds as any[],
                    fees.id.toString() as any
                  ) as [],
                })
              }
            />
          ))) ||
        ""}

      {(!!filters.openSunday ||
        !!filters.openMonday ||
        !!filters.openTuesday ||
        !!filters.openWednesday ||
        !!filters.openThursday ||
        !!filters.openFriday ||
        !!filters.openSaturday) &&      
        dayOptions
        .filter((item) => (
          (item === "Sunday" && filters.openSunday && filters.openSunday.length) || 
          (item === "Monday" && filters.openMonday && filters.openMonday.length) || 
          (item === "Tuesday" && filters.openTuesday && filters.openTuesday.length) || 
          (item === "Wednesday" && filters.openWednesday && filters.openWednesday.length) || 
          (item === "Thursday" && filters.openThursday && filters.openThursday.length) || 
          (item === "Friday" && filters.openFriday && filters.openFriday.length) || 
          (item === "Saturday" && filters.openSaturday && filters.openSaturday.length))  
      )
        .map((dow) => 
          <DesktopControlToggle
            key={dow}
            name={t(`hoursValues${dow.toLowerCase()}`)}
            onClick={() =>
              setFilters(toggleDayFilters(filters, dow))
            }         
          />            
      )}

      {(!!filters.languageIds &&
        filters.languageIds.length &&
        languageOptions &&
        languageOptions.length &&
        languageOptions
          .filter(
            (item) =>
              filters.languageIds &&
              filters.languageIds.length &&
              item.id !== undefined &&
              filters.languageIds.includes(item.id.toString())
          )
          .map((language: optionWithId) => (
            <DesktopControlToggle
              key={language.id}
              name={t(language.translate)}
              onClick={() =>
                setFilters({
                  ...(filters as SearchFilters),
                  languageIds: toggleItemInFilterList(
                    filters.languageIds as any[],
                    language.id.toString()
                  ) as [],
                })
              }
            />
          ))) ||
        ""}

      {(!!filters.serviceProvidedIds &&
        filters.serviceProvidedIds.length &&
        responseServices &&
        responseServices.length &&
        responseServices
          .filter(
            (item) =>
              filters.serviceProvidedIds &&
              filters.serviceProvidedIds.length &&
              item.id !== undefined &&
              (filters.serviceProvidedIds.some(id => item.id.split(",").includes(id)) ||
              filters.serviceProvidedIds?.includes(item.id))
          )
          .map((serviceProvided: ResponseServiceProvided) => (
            <DesktopControlToggle
              key={serviceProvided.id}
              name={serviceProvided.text}
              onClick={() => {
                setFilters({
                  ...(filters as SearchFilters),
                  serviceProvidedIds: toggleItemsInFilterList(
                    filters.serviceProvidedIds as string[],
                    serviceProvided.id.split(",")
                  ) as [],
                });
                if (serviceProvided.categoryId === 1500103) {// Crisis services category
                  setCrisisSelectedValues(
                    toggleItemsInFilterList(
                      crisisSelectedValues as string[],
                      serviceProvided.id.split(",")
                    )
                  );
                }
              }}
            />
          ))) ||
        ""}

      {(!!filters.patientCommunityIds &&
        filters.patientCommunityIds.length &&
        communityOptions &&
        communityOptions.length &&
        communityOptions
          .filter(
            (item) =>
              filters.patientCommunityIds &&
              filters.patientCommunityIds.length &&
              item.id !== undefined &&
              filters.patientCommunityIds.includes(item.id.toString())
          )
          .map((community: optionWithId) => (
            <DesktopControlToggle
              key={community.id}
              name={t(community.translate)}
              onClick={() => {
                setFilters({
                  ...(filters as SearchFilters),
                  patientCommunityIds: toggleItemInFilterList(
                    filters.patientCommunityIds as any[],
                    community.id.toString()
                  ) as [],
                });
              }}
            />
          ))) ||
        ""}

      {(!!filters.telehealthOptions &&
        filters.telehealthOptions.length &&
        telehealthOptions &&
        telehealthOptions.length &&
        telehealthOptions
          .filter(
            (item) =>
              filters.telehealthOptions &&
              filters.telehealthOptions.length &&
              item.translate !== undefined &&
              filters.telehealthOptions.includes(item.lineageText)
          )
          .map((telehealth: optionWithId) => (
            <DesktopControlToggle
              key={telehealth.translate}
              name={t(telehealth.translate)}
              onClick={() =>
                setFilters({
                  ...(filters as SearchFilters),
                  telehealthOptions: toggleItemInFilterList(
                    filters.telehealthOptions as any[],
                    telehealth.lineageText
                  ) as []
                })
              }
            />
          ))) ||
        ""}

      <Button
        unstyled
        className="width-auto"
        type="button"
        onClick={() => {
          setFilters(getFiltersWithOptionalCleared(filters));
          logEvent(AnalyticsAction.ApplyFilter, {
            label: "Clear filters button",
          });
          setCrisisSelectedValues([]);
        }}
      >
        {t("clearAll")}
      </Button>
    </Grid>
  );
}

export default ControlToggles;
